import { useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Box, HStack, IconButton, ScrollView, Spacer, VStack, View, useMediaQuery, useTheme } from 'native-base';
import { filter, find, flatMap, isEmpty, map, range, startCase, toLower } from 'lodash';

import {
  ArrowSquareUpRightIcon,
  Button,
  ButtonGroup,
  CalendarClockIcon,
  ConnectedDevicesIcon,
  MinusCircleIcon,
  TargetIcon,
  Text,
} from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import {
  AggAssignedTaskDto,
  AggCleaningTask,
  AggPositionAssigneeDto,
  AggPositionCleaningTaskItemDto,
  AggPositionDetailsDto,
  AggPositionResponsibilityDto,
  AggPositionSlotDto,
  CleaningScheduleTypeEnum,
} from '@pimm/services/lib/sms-workforce';
import { useSiteTime } from '@app/features/store-core';
import { formatToTimeOnly } from '@app/utils/date-formatter';
import { hexToRGBA } from '@app/utils/string-formatter';
import { TimeDisplay } from './time-display';
import { StarIcon } from '../icons';
import { CleaningTasks } from './cleaning-tasks';
import { useKitchenPositionGuideLive } from '../context';

type ShiftScopeProps = {
  positionSlot: AggPositionSlotDto;
  onClose?: () => void;
  onNavigateTo: (screen: string, params?: any) => void;
};

export const ShiftScope = ({ positionSlot, onNavigateTo, ...props }: ShiftScopeProps) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isSmallScreen] = useMediaQuery({ maxWidth: 1200 });
  const { translate } = useAppLocale();
  const livePositionGuide = useKitchenPositionGuideLive();
  const siteTime = useSiteTime();
  const [assignee] = useState<AggPositionAssigneeDto | undefined>(positionSlot.positionAssignee);
  const [primaryJob] = useState<AggPositionDetailsDto | undefined>(positionSlot.positionJob?.primaryJob);
  const [secondaryResponsibilities] = useState<AggPositionDetailsDto[]>(
    map(positionSlot.positionJob?.secondaryJobs, _job => ({ ..._job, responsibilities: filter(_job.responsibilities, _ => !_.isPrimary) })),
  );

  const [tabFocus, setTabFocus] = useState<number>(0);
  const dailyGoalRef = useRef<HTMLDivElement>(null);
  const cleaningTaskRef = useRef<HTMLDivElement>(null);

  const memomizedData = useMemo(() => {
    const data = livePositionGuide.data;

    const nonServicePlan = flatMap(data?.opsTaskFlowChartWidget, ({ phase, phaseTranslations, positions }) =>
      positions
        ?.filter(position => position.employeeId === assignee?.employeeId)
        .map(({ title, translations, tasksStartTime }) => ({
          phase,
          phaseTranslations,
          title,
          translations,
          startTime: tasksStartTime,
        })),
    );

    // Build weekDays map (English day name → Date) for position task scope filtering
    const siteNow = siteTime.today();
    const now = moment(siteNow);
    const weekStart = siteTime.toStartEndOfWeek(siteNow)?.startDate;
    const weekDays: Record<string, Date> = weekStart
      ? Object.fromEntries(
          range(7).map(i => {
            const day = moment(weekStart).add(i, 'days');
            return [day.locale('en').format('dddd'), day.toDate()];
          }),
        )
      : {};

    // inScopeForShift: only today + past days (no future days shown in modal)
    const isPosInScope = (t: AggPositionCleaningTaskItemDto): boolean => {
      if (t.dayOfWeek && weekDays[t.dayOfWeek]) return moment(weekDays[t.dayOfWeek]).isSameOrBefore(now, 'day');
      if (t.dueDate) return moment(t.dueDate.replace('Z', '')).isSameOrBefore(now, 'day');
      return false;
    };
    const isEmpInScope = (t: AggAssignedTaskDto): boolean => {
      if (t.dueDate) return moment(t.dueDate.replace('Z', '')).isSameOrBefore(now, 'day');
      return false;
    };

    // Derive effective dueDate for position tasks that have no explicit dueDate.
    // Date part: YYYY-MM-DD from weekDays (store-local).
    // Time part: scheduleEndTime concatenated directly — no moment arithmetic
    // that could shift hours across browser/store timezone boundaries.
    const getEffectiveDueDate = (t: AggPositionCleaningTaskItemDto): string | undefined => {
      if (t.dueDate) return t.dueDate;
      if (t.dayOfWeek && weekDays[t.dayOfWeek]) {
        const dateStr = moment(weekDays[t.dayOfWeek]).format('YYYY-MM-DD');
        if (t.scheduleEndTime) return `${dateStr}T${t.scheduleEndTime}`;
        return `${dateStr}T23:59:59`;
      }
      return undefined;
    };

    const SCHEDULE_ORDER = [CleaningScheduleTypeEnum.Daily, CleaningScheduleTypeEnum.Weekly, CleaningScheduleTypeEnum.Monthly];

    // Position tasks for this slot — filtered by inScopeForShift, with derived dueDates
    const posEntry = data?.positionCleaningTasks?.find(e => e.slotNumber === positionSlot.slotNumber);
    const rawPosTasks = posEntry?.tasks ?? [];
    const posTaskIds = new Set(rawPosTasks.map(t => t.cleaningTaskId));

    const positionScheduleTypes = SCHEDULE_ORDER.map(st => {
      const tasks: AggAssignedTaskDto[] = rawPosTasks
        .filter(t => {
          if ((t.scheduleType ?? CleaningScheduleTypeEnum.Daily) !== st || !isPosInScope(t)) return false;
          return true;
        })
        .map(
          t =>
            ({
              cleaningTaskAssignmentId: t.cleaningTaskAssignmentId,
              cleaningTaskId: t.cleaningTaskId,
              categoryTitle: translate(t.categoryTitle, t?.categoryTitleTranslations),
              title: translate(t.title, t?.titleTranslations),
              dueDate: getEffectiveDueDate(t),
              cleaningTaskStatus: t.cleaningTaskStatus,
              statusDate: t.statusDate,
              linkedDoc: t.linkedDoc,
              positionTitle: translate(posEntry?.positionTitle, posEntry?.positionTitleTranslations) ?? undefined,
              firstName: t.firstName,
              lastName: t.lastName,
            } as AggAssignedTaskDto),
        );
      return { scheduleType: st, assignedTasks: tasks };
    });
    const positionTasks: AggCleaningTask | undefined = positionScheduleTypes.some(s => (s.assignedTasks?.length ?? 0) > 0)
      ? { scheduleTypes: positionScheduleTypes }
      : undefined;

    // Employee tasks — deduped against position tasks, filtered by inScopeForShift
    const empEntry = data?.cleaningTasks?.find(s => s?.employeeId && assignee?.employeeId && s.employeeId === assignee.employeeId);
    const empScheduleTypes = SCHEDULE_ORDER.map(st => {
      const tasks =
        (empEntry?.scheduleTypes ?? [])
          .find(s => s.scheduleType === st)
          ?.assignedTasks?.filter(t => !posTaskIds.has(t.cleaningTaskId ?? undefined) && isEmpInScope(t)) ?? [];
      return { scheduleType: st, assignedTasks: tasks };
    });
    const empTasks: AggCleaningTask | undefined = empScheduleTypes.some(s => (s.assignedTasks?.length ?? 0) > 0)
      ? { employeeId: assignee?.employeeId, scheduleTypes: empScheduleTypes }
      : undefined;

    return {
      nonServicePlan,
      positionTasks,
      empTasks,
      positionTitle: translate(posEntry?.positionTitle, posEntry?.positionTitleTranslations) ?? undefined,
      slotHasEmployee: !!assignee?.employeeId,
    };
  }, [livePositionGuide.data, assignee, positionSlot.slotNumber]);

  const isEmptyNonService = isEmpty(memomizedData?.nonServicePlan);
  const primaryResponsibilities = find<AggPositionResponsibilityDto>(primaryJob?.responsibilities, _ => !!_.isPrimary);
  const isEmptyResponsibilities = isEmpty(primaryResponsibilities) && isEmpty(secondaryResponsibilities);
  const isEmptyPositionGoals = isEmpty(primaryJob?.positionalGoals);

  const handleTabClick = (index: number): void => {
    setTabFocus(index);
    (index ? cleaningTaskRef : dailyGoalRef).current?.scrollIntoView({ behavior: 'smooth' });
  };

  const color = primaryJob?.color ?? colors.gray[200];

  return (
    <VStack w="full" h="full">
      <HStack alignItems="center" px={4} h="56px" borderBottomWidth={1}>
        <Text size="xl" fontWeight={700} color="gray.900">
          {t('position_guide:shift_scope')}
        </Text>
      </HStack>

      <HStack flex={1}>
        <HStack width="full">
          {/* Master Content */}
          <ScrollView flex={1} borderRightWidth={1} scrollEnabled borderColor="gray.200" maxW={{ md: '1/3', xl: '1/4' }}>
            <VStack space={4} p={4}>
              <VStack rounded="xl" space={2} p={1.5} borderWidth={1} borderColor={hexToRGBA(color, 0.4)} bg={hexToRGBA(color, 0.2)}>
                <HStack alignItems="center" space={1.5} pr={0.5}>
                  {positionSlot.slotNumber && primaryJob?.positionType !== 'NonService' && (
                    <Box rounded="md" alignContent="center" justifyContent="center" h={6} w={7} bg={hexToRGBA(color, 0.4)}>
                      <Text size="lg" color="black" fontWeight={700} textAlign="center">
                        {positionSlot.slotNumber ?? ''}
                      </Text>
                    </Box>
                  )}

                  <HStack flex={1} alignItems="center" h="full">
                    {primaryJob?.title ? (
                      <Box pl={1}>
                        <Text size="lg" fontWeight={700} color="black" lineHeight="lg" numberOfLines={1}>
                          {translate(primaryJob?.title, primaryJob?.translations)}
                        </Text>
                      </Box>
                    ) : (
                      <Box rounded="md" justifyContent="center" px={2.5} w="full" h="full" bg="gray.700">
                        <Text size="md" fontWeight={700} color="white" lineHeight="md">
                          {t('position_guide:no_position_assigned')}
                        </Text>
                      </Box>
                    )}
                  </HStack>
                  {/* TODO: Need to confirm what the condition will be for this. */}
                  {primaryJob?.positionType === 'NonService' && (
                    <Button
                      backgroundColor="white"
                      px={2}
                      minH={7}
                      variant="unstyled"
                      shadow={1}
                      endIcon={<CalendarClockIcon size={4} color="gray.600" />}
                      onPress={() => onNavigateTo('DailyPlan')}
                    >
                      {t('position_guide:position_plan')}
                    </Button>
                  )}
                  {!!positionSlot.isOpsLeader && (
                    <Box flex="none" rounded="full" p="2px" borderWidth={1} borderColor="white" bg="warning.300">
                      <StarIcon size={3.5} />
                    </Box>
                  )}
                </HStack>

                {/* Assignee */}
                <VStack
                  position="relative"
                  justifyContent="center"
                  rounded="lg"
                  py={1.5}
                  px={2}
                  minHeight="48px"
                  borderWidth={1}
                  borderColor={hexToRGBA(color, 0.5)}
                  bg="white"
                >
                  {!!assignee ? (
                    <VStack space={1}>
                      <Text size="lg" fontWeight={700} color="black" lineHeight="xs" numberOfLines={1} ellipsizeMode="tail">
                        {startCase(toLower(assignee.name))}
                      </Text>

                      <HStack space={1} alignItems="center">
                        {!!assignee?.startTime && !!assignee?.endTime && (
                          <TimeDisplay
                            dayBlock={siteTime.toDayBlockNow()}
                            startTime={assignee.startTime}
                            endTime={assignee.endTime}
                            keepAlive
                          />
                        )}

                        <Spacer />
                      </HStack>
                    </VStack>
                  ) : (
                    <HStack alignItems="center">
                      <Text size="lg" fontWeight={600} color="black" lineHeight="xs" textAlign="center">
                        {t('position_guide:no_employee_assigned')}
                      </Text>
                      <Spacer />
                      {/* TODO: Confirm what would be the condition to show this */}
                      {primaryJob?.positionType !== 'NonService' && (
                        <Button
                          size="sm"
                          px={2}
                          minH={7}
                          variant="unstyled"
                          shadow={1}
                          endIcon={<CalendarClockIcon size={4} color="gray.600" />}
                          onPress={() => onNavigateTo('DailyPlan')}
                        >
                          {t('position_guide:position_plan')}
                        </Button>
                      )}
                    </HStack>
                  )}
                </VStack>
              </VStack>

              {/* Position Responsibilities */}
              <VStack space={!isEmptyResponsibilities ? 1 : 0}>
                <Text size="lg" fontWeight={700} color="black" lineHeight="2xl">
                  {t('position_guide:position_responsibilities')}
                </Text>
                {!isEmptyResponsibilities ? (
                  <VStack space={4}>
                    {/* Primary Position */}
                    {!isEmpty(primaryResponsibilities) && (
                      <VStack rounded="lg" p={2.5} borderWidth={1} bg="gray.25" space={2}>
                        <Text size="md" fontWeight={700} color="black" lineHeight="xs">
                          {t('position_guide:primary_position')}
                        </Text>
                        <Box flex={1} rounded="lg" p={2.5} borderWidth={1} bg="white" minH={10} justifyContent="center">
                          <Text size="md" fontWeight={500} color="black" lineHeight="xs" numberOfLines={12} ellipsizeMode="tail">
                            {translate(primaryResponsibilities?.responsibility, primaryResponsibilities?.translations)?.replace('|', '\n')}
                          </Text>
                        </Box>
                      </VStack>
                    )}

                    {/* Secondary Position(s) */}
                    {!isEmpty(secondaryResponsibilities) && (
                      <VStack rounded="lg" p={2.5} borderWidth={1} bg="gray.25" space={2}>
                        <Text size="md" fontWeight={700} color="black" lineHeight="xs">
                          {t('position_guide:secondary_position_s')}
                        </Text>
                        <VStack space={2}>
                          {map(secondaryResponsibilities, _position => (
                            <Box
                              key={`secondary-jobs[${_position.id}]`}
                              rounded="lg"
                              flex={1}
                              p={2.5}
                              borderWidth={1}
                              bg="white"
                              minH="auto"
                              justifyContent="center"
                              opacity={_position ? 1 : 0.5}
                            >
                              <Text size="md" fontWeight={700} color="black" lineHeight="sm" numberOfLines={3} ellipsizeMode="tail">
                                {translate(_position?.title, _position?.translations)}
                              </Text>
                              {map(_position?.responsibilities, _responsibility => (
                                <Text
                                  size="md"
                                  fontWeight={500}
                                  color="black"
                                  lineHeight="xs"
                                  numberOfLines={12}
                                  ellipsizeMode="tail"
                                  pt={2}
                                >
                                  {translate(_responsibility.responsibility, _responsibility?.translations)}
                                </Text>
                              ))}
                            </Box>
                          ))}
                        </VStack>
                      </VStack>
                    )}
                  </VStack>
                ) : (
                  <Box py={2}>
                    <Text size="md" fontWeight={500} color="gray.600" lineHeight="lg">
                      {t('position_guide:empty_message_responsibilities')}
                    </Text>
                  </Box>
                )}
              </VStack>
            </VStack>
          </ScrollView>

          {/* Detail/Tab Content */}
          <VStack flex={1}>
            {isSmallScreen && (
              <HStack space={2} justifyContent="flex-start" px={4} py={2} borderBottomWidth={1}>
                <ButtonGroup value={tabFocus} onChange={index => handleTabClick(index)}>
                  <ButtonGroup.Item alignItems="center" value={0} minW={120}>
                    {t('position_guide:daily_positional_goals')}
                  </ButtonGroup.Item>
                  <ButtonGroup.Item alignItems="center" value={1}>
                    {t('position_guide:cleaning_task_schedule')}
                  </ButtonGroup.Item>
                </ButtonGroup>
              </HStack>
            )}
            <HStack flex={1}>
              {/* Left Content */}
              {(!isSmallScreen || tabFocus === 0 || tabFocus === 1) && (
                <ScrollView
                  flex={1}
                  my={4}
                  scrollEnabled
                  contentContainerStyle={{
                    flexGrow: isEmptyResponsibilities || isEmptyNonService ? 1 : 0,
                  }}
                  maxW={{ md: 'full', xl: 'md' }}
                >
                  <VStack flex={1} space={4} px={4} ref={dailyGoalRef}>
                    {/* Daily Positional Goal */}
                    {primaryJob?.positionType !== 'NonService' && (
                      <VStack
                        rounded="lg"
                        p={2.5}
                        borderWidth={1}
                        bg="gray.25"
                        space={2}
                        minH={!isEmptyPositionGoals || !isSmallScreen ? 0 : '1/3'}
                      >
                        <HStack space={2} alignItems="center">
                          <TargetIcon size="md" color="black" />
                          <Text size="lg" fontWeight={700} color="black" lineHeight="2xl">
                            {t('position_guide:daily_positional_goals')}
                          </Text>
                          <Spacer />
                          <IconButton
                            p={0}
                            rounded="md"
                            w={8}
                            h={8}
                            borderWidth={1}
                            borderColor="gray.300"
                            backgroundColor="white"
                            _hover={{ bg: undefined }}
                            _pressed={{ bg: undefined }}
                            icon={<ArrowSquareUpRightIcon color="gray.700" />}
                            onPress={() => onNavigateTo('OpsPlan')}
                          />
                        </HStack>
                        {!isEmptyPositionGoals ? (
                          <Box flex={1} rounded="lg" p={2.5} borderWidth={1} bg="white" minH={10}>
                            <VStack space={1}>
                              <Text size="md" fontWeight={500} color="black" lineHeight="xs" numberOfLines={12} ellipsizeMode="tail">
                                {map(primaryJob?.positionalGoals, goal => translate(goal.description, goal.translations)).join('\n')}
                              </Text>
                              <HStack alignItems="center" space={1}>
                                <ConnectedDevicesIcon size="12px" color="gray.500" />
                                <Text size="md" fontWeight={500} color="gray.700" lineHeight="xs">
                                  {translate(primaryJob?.title, primaryJob?.translations)}
                                </Text>
                              </HStack>
                            </VStack>
                          </Box>
                        ) : (
                          <Box flex={1} justifyContent="center">
                            <Text
                              size="md"
                              fontWeight={500}
                              color="gray.700"
                              textAlign={!isEmptyPositionGoals ? 'left' : 'center'}
                              padding={2}
                              lineHeight="sm"
                            >
                              {t('position_guide:no_assigned_goals')}
                            </Text>
                          </Box>
                        )}
                      </VStack>
                    )}

                    {/* Non-Service Plan */}
                    <VStack
                      rounded="lg"
                      p={2.5}
                      borderWidth={1}
                      bg="gray.25"
                      flex={isEmptyNonService ? 1 : 0}
                      minH={isSmallScreen && isEmptyNonService ? '1/4' : 'fit-content'}
                      height="full"
                      space={2}
                    >
                      <HStack space={2} alignItems="center">
                        <MinusCircleIcon size="md" color="black" />
                        <Text size="lg" fontWeight={700} color="black" lineHeight="2xl">
                          {t('position_guide:non_service_plan')}
                        </Text>
                        <Spacer />
                        <IconButton
                          p={0}
                          rounded="md"
                          w={8}
                          h={8}
                          borderWidth={1}
                          borderColor="gray.300"
                          backgroundColor="white"
                          _hover={{ bg: undefined }}
                          _pressed={{ bg: undefined }}
                          icon={<ArrowSquareUpRightIcon color="gray.700" />}
                          onPress={() => onNavigateTo('DailyPlan', { tabFocus: 'non-service' })}
                        />
                      </HStack>

                      {!isEmptyNonService ? (
                        <VStack rounded="lg" borderWidth={1} overflow="hidden">
                          <HStack alignItems="center" minH={8} bg="gray.200" space={1} flex={1} px={2.5}>
                            <Box w="1/3" flex={1}>
                              <Text size="md" fontWeight={500} color="gray.900" lineHeight="xs">
                                {t('position_guide:phase')}
                              </Text>
                            </Box>
                            <Box w="1/3">
                              <Text size="md" fontWeight={500} color="gray.900" lineHeight="xs">
                                {t('position_guide:position')}
                              </Text>
                            </Box>
                            <Box maxW={88} flex={1}>
                              <Text size="md" fontWeight={500} color="gray.900" lineHeight="xs">
                                {t('position_guide:start_time')}
                              </Text>
                            </Box>
                          </HStack>
                          {memomizedData?.nonServicePlan?.map((nonService, index) => (
                            <HStack
                              bg="white"
                              key={index}
                              alignItems="center"
                              minH={12}
                              borderTopWidth={index ? 1 : 0}
                              flex={1}
                              space={1}
                              px={2.5}
                            >
                              <Box w="1/3" flex={1}>
                                <Text size="md" fontWeight={500} color="gray.900" lineHeight="xs" numberOfLines={2} ellipsizeMode="tail">
                                  {translate(nonService?.phase, nonService?.phaseTranslations)}
                                </Text>
                              </Box>
                              <Box justifyContent="center" w="1/3">
                                <Text size="md" fontWeight={500} color="gray.900" lineHeight="xs" numberOfLines={2} ellipsizeMode="tail">
                                  {translate(nonService?.title, nonService?.translations)}
                                </Text>
                              </Box>
                              <Box maxW={88} flex={1}>
                                <Text size="md" fontWeight={500} color="gray.900" lineHeight="xs">
                                  {formatToTimeOnly(nonService?.startTime) ?? '--'}
                                </Text>
                              </Box>
                            </HStack>
                          ))}
                        </VStack>
                      ) : (
                        <Box flex={1} justifyContent="center">
                          <Text size="md" fontWeight={500} color="gray.700" textAlign="center">
                            {t('position_guide:no_assigned_tasks')}
                          </Text>
                        </Box>
                      )}
                    </VStack>

                    {/* Cleaning Task */}
                    {isSmallScreen && (
                      <VStack rounded="lg" borderWidth={1} bg="gray.25" space={2} ref={cleaningTaskRef} minH="1/3">
                        <CleaningTasks
                          assignee={assignee}
                          empTasks={memomizedData.empTasks}
                          positionTasks={memomizedData.positionTasks}
                          positionTitle={memomizedData.positionTitle}
                          slotHasEmployee={memomizedData.slotHasEmployee}
                          isSmallScreen={isSmallScreen}
                          onNavigateTo={onNavigateTo}
                        />
                      </VStack>
                    )}
                  </VStack>
                </ScrollView>
              )}

              {!isSmallScreen && (
                <View flex={1} borderLeftWidth={isSmallScreen ? 0 : 1}>
                  <CleaningTasks
                    assignee={assignee}
                    empTasks={memomizedData.empTasks}
                    positionTasks={memomizedData.positionTasks}
                    positionTitle={memomizedData.positionTitle}
                    slotHasEmployee={memomizedData.slotHasEmployee}
                    isSmallScreen={isSmallScreen}
                    onNavigateTo={onNavigateTo}
                  />
                </View>
              )}
            </HStack>
          </VStack>
        </HStack>
      </HStack>
    </VStack>
  );
};
